import { Plus, ScrollText } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { RuleDefinition } from '@/types';

const SEV_COLOR: Record<string, string> = {
  critical: 'text-crit',
  warning: 'text-warn',
  info: 'text-info',
};

export function RuleList({
  rules,
  loading,
  error,
  selectedId,
  dirtyId,
  onSelect,
  onNew,
}: {
  rules: RuleDefinition[];
  loading: boolean;
  error?: string;
  selectedId: string | null;
  /** The id currently being edited with unsaved changes. */
  dirtyId?: string | null;
  onSelect: (rule: RuleDefinition) => void;
  onNew: () => void;
}) {
  return (
    <div className="flex h-full flex-col border-r border-hairline">
      <div className="flex items-center justify-between border-b border-hairline px-3 py-2">
        <span className="eyebrow">rules {rules.length > 0 && `· ${rules.length}`}</span>
        <button
          type="button"
          onClick={onNew}
          className="inline-flex items-center gap-1 border border-accent/40 bg-accent/10 px-2 py-1 font-condensed text-[10px] font-semibold uppercase tracking-wider text-accent hover:bg-accent/20"
        >
          <Plus size={11} /> New
        </button>
      </div>

      <div className="flex-1 overflow-auto">
        {loading && <Msg>Loading rules…</Msg>}
        {error && !loading && <Msg tone="crit">{error}</Msg>}
        {!loading && !error && rules.length === 0 && (
          <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
            <ScrollText size={20} className="text-text-faint" />
            <p className="text-[12px] text-text-dim">No rules yet. Create the first one.</p>
          </div>
        )}

        {rules.map((r) => {
          const active = r.id === selectedId;
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => onSelect(r)}
              className={cn(
                'flex w-full items-start gap-2 border-b border-hairline/60 px-3 py-2 text-left hover:bg-surface-raised/60',
                active && 'bg-surface-raised',
              )}
            >
              <span className={cn('mt-[5px] shrink-0 text-[8px]', SEV_COLOR[r.severity ?? 'info'])}>●</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-sans text-[12.5px] text-text">
                  {r.name || <span className="text-text-faint">(unnamed)</span>}
                  {dirtyId === r.id && <span className="ml-1 text-accent">•</span>}
                </span>
                <span className="block truncate font-mono text-[10px] text-text-faint">{r.id}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Msg({ children, tone }: { children: React.ReactNode; tone?: 'crit' }) {
  return (
    <div className={cn('px-3 py-4 text-[12px]', tone === 'crit' ? 'text-crit' : 'text-text-dim')}>
      {children}
    </div>
  );
}
